import { useParameterStore } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { ItemReferenceParams } from "@/types/itemReferenceTypes";
import Select, { StylesConfig, MultiValue, MultiValueProps, components } from "react-select";
import { SelectableParameter } from "./GenericParameterSelect";
import { SelectedRefAndParameter } from "@/frontend/hooks/parameters";

type ParameterOption = {
  label: string;
  value: string;
  parameter: SelectableParameter;
  reference: ItemReferenceParams | null;
};

// Match Bootstrap's form-control-sm sizing so this control lines up with the
// adjacent Title/Identifier inputs.
const SM_INPUT_HEIGHT = 'calc(1.5em + 0.5rem + 2px)';

const compactStyles: StylesConfig<ParameterOption, true> = {
  control: (base) => ({
    ...base,
    minHeight: SM_INPUT_HEIGHT,
    height: 'auto',
    fontSize: '0.875rem',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 0.5rem',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    minHeight: SM_INPUT_HEIGHT,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 4px',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '0 4px',
  }),
  multiValue: (base) => ({
    ...base,
    margin: '2px 2px 2px 0',
  }),
};

// Selected chips truncate long titles, so expose the full title as a native
// tooltip on hover.
function MultiValueWithTitle(props: MultiValueProps<ParameterOption, true>) {
  const { innerProps, data } = props;
  return <components.MultiValue {...props} innerProps={{ ...innerProps, title: data.label }} />;
}

// Defined once so react-select doesn't remount the chips on every render.
const selectComponents = { MultiValue: MultiValueWithTitle };

export function MultiParameterSelect(props: {
  parameters: SelectableParameter[];
  currentSelections: SelectedRefAndParameter[];
  createReference: (parameter: SelectableParameter) => void;
}) {
  const { parameters, currentSelections, createReference } = props;
  const deleteReference = useParameterStore(state => state.deleteReference);

  const options = parameters.map(param => ({
    label: param.title,
    value: `${param.id}-${param.keyId ?? ''}`,
    parameter: param,
    reference: null,
  }));

  const value = currentSelections.map(({ currentParameter, currentReference }) => ({
    label: currentParameter.title,
    value: `${currentParameter.id}-${currentParameter.keyId ?? ''}`,
    parameter: currentParameter,
    reference: currentReference
  }));

  return (
    <Select<ParameterOption, true>
      aria-label={I18n.get('selectParameters')}
      isMulti
      value={value}
      options={options}
      isClearable={false}
      styles={compactStyles}
      components={selectComponents}
      onChange={(newValue: MultiValue<ParameterOption>, actionMeta) => {
        if (actionMeta.action === 'select-option' && actionMeta.option) {
          createReference(actionMeta.option.parameter);
        }
        if (actionMeta.action === 'remove-value' && actionMeta.removedValue) {
          void deleteReference(actionMeta.removedValue.reference?.id);
        }
      }}
    />
  );
}
